import { UI, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { useBreakpointValue, Utils } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import React, { useCallback } from 'react';
import { ScrollView } from 'react-native';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../../../../routes/types';
import { customerHasTermAvailableQuery } from '../../../../../../store/customer/selector';
import { AssetSelectedTypeMap } from '../../../../../../store/product/types';
import { FeatureToggle, useUnleashFlags } from '../../../../../../utils/useUnleashFlags';
import { CustomerDetailTabsKeys } from '../../../../constants';

import { ClubsApplication } from './products/ClubsApplication';
import { CoesApplication } from './products/CoesApplication';
import { DirectTreasuryApplication } from './products/DirectTreasuryApplication';
import { EquityApplication } from './products/EquityApplication';
import { FixedIncomeApplication } from './products/FixedIncomeApplication';
import { FundsApplication } from './products/FundsApplication';
import { PrivatePensionKindSelectorModal } from './products/PrivatePensionApplication/components/PrivatePensionKindSelectorModal';
import { PublicOfferApplication } from './products/PublicOfferApplication';
import { CustomerDetailWithSimplifiedApplicationFlags } from './types';

export const CustomerApplicationModal: React.FC = () => {
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'CustomerApplicationModal'>>();
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { b2bShowSimplifiedApplication } = useUnleashFlags(FeatureToggle.SimplifiedApplication);

  const { data: customerHasTermAvailable, loading: customerTermLoading } = useRecoilQuery(
    customerHasTermAvailableQuery({
      accountId: params.accountId,
      marketType: params.productType,
      // @ts-expect-error productIdForTerm is guaranteed to be available when we needed
      productIdForTerm: params.offer.productIdForTerm,
    }),
    { if: () => !!b2bShowSimplifiedApplication }
  );

  const normalizedParams: CustomerDetailWithSimplifiedApplicationFlags = {
    ...params,
    isSimplifiedApplicationFlagMatched: b2bShowSimplifiedApplication,
    clientHasTerm: !!customerHasTermAvailable,
    isSimplifiedApplication: !!customerHasTermAvailable && b2bShowSimplifiedApplication,
    isClientTermLoading: customerTermLoading,
    showSendingChannels:
      !b2bShowSimplifiedApplication || (b2bShowSimplifiedApplication && !customerHasTermAvailable),
  };

  const responsiveWidth = useBreakpointValue({
    base: 1,
    sm: 1,
    md: 0.42,
  });

  const goBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleFollowTicket = () => {
    navigation.replace('AppDrawer', {
      screen: 'CustomerDetailGroup',
      params: {
        screen: 'CustomerDetail',
        params: {
          ...normalizedParams,
          selectedTab: CustomerDetailTabsKeys.Transactions,
        },
      },
    });
  };

  const renderCorrectRedemptionProduct = () => {
    switch (params.productType) {
      case 'fixedIncome':
        return (
          <FixedIncomeApplication
            {...normalizedParams}
            onClose={goBack}
            handleFollowTicket={handleFollowTicket}
            offer={params.offer as AssetSelectedTypeMap['fixedIncome']}
            productType="fixedIncome"
          />
        );

      case 'funds':
        return (
          <FundsApplication
            {...normalizedParams}
            onClose={goBack}
            handleFollowTicket={handleFollowTicket}
            offer={params.offer as AssetSelectedTypeMap['funds']}
            productType="funds"
          />
        );

      case 'directTreasury':
        return (
          <DirectTreasuryApplication
            {...normalizedParams}
            onClose={goBack}
            handleFollowTicket={handleFollowTicket}
            offer={params.offer as AssetSelectedTypeMap['directTreasury']}
            productType="directTreasury"
          />
        );

      case 'equity':
        return (
          <EquityApplication
            {...normalizedParams}
            onClose={goBack}
            handleFollowTicket={handleFollowTicket}
            offer={params.offer as AssetSelectedTypeMap['equity']}
            productType="equity"
          />
        );

      case 'coes':
        return (
          <CoesApplication
            {...normalizedParams}
            onClose={goBack}
            handleFollowTicket={handleFollowTicket}
            offer={params.offer as AssetSelectedTypeMap['coes']}
            productType="coes"
          />
        );

      case 'publicOffers':
        return (
          <PublicOfferApplication
            {...normalizedParams}
            onClose={goBack}
            offer={params.offer as AssetSelectedTypeMap['publicOffers']}
            productType="publicOffers"
          />
        );

      case 'clubs':
        return (
          <ClubsApplication
            {...normalizedParams}
            onClose={goBack}
            offer={params.offer as AssetSelectedTypeMap['clubs']}
            productType="clubs"
          />
        );

      case 'privatePension':
        return (
          <PrivatePensionKindSelectorModal
            {...normalizedParams}
            onClose={goBack}
            offer={params.offer as AssetSelectedTypeMap['privatePension']}
            accountId={params.accountId}
            accountNumber={params.accountNumber}
            customerId={params.customerId}
            cpfCnpj={Utils.Unmask.CpfOrCnpj(params.cpfCnpjFormatted)}
            productType="privatePension"
          />
        );

      default:
        return null;
    }
  };

  return (
    <UI.SliderModal width={responsiveWidth} onDismiss={goBack}>
      <ScrollView>
        <VStack p={Spacing['inset.sm']}>{renderCorrectRedemptionProduct()}</VStack>
      </ScrollView>
    </UI.SliderModal>
  );
};
